import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Result } from "antd";
import { useApolloClient } from "@apollo/client";
import AnimatedLogo from "../components/AnimatedLogo";
import { GET_QR_ASSIGNMENTS_DETAIL } from "../graphql/queries";
import { GeoLocation, Place, PlaceWithDistance } from "../types";

interface QrAssignmentsDetail {
  id?: string | null;
  qr_id?: string | null;
  geo_location?: GeoLocation | null;
  latitude?: number | null;
  longitude?: number | null;
  event_id?: string | null;
  event_name?: string | null;
  place_id?: string | null;
  place_name?: string | null;
  inventory_id?: string | null;
  inventory_name?: string | null;
  note?: string | null;
}

function geoFromQrDetail(detail: QrAssignmentsDetail): GeoLocation | null {
  const g = detail.geo_location;
  if (g?.latitude != null && g?.longitude != null) {
    return { latitude: g.latitude, longitude: g.longitude };
  }
  if (detail.latitude != null && detail.longitude != null) {
    return { latitude: detail.latitude, longitude: detail.longitude };
  }
  return null;
}

interface QrRouteResolverProps {
  setInventory: (inventory: string[]) => void;
  setIsOneInventory: (isOne: boolean) => void;
  setPlaceDetail: (place: Place | null) => void;
  setPlacesForMap: (places: PlaceWithDistance[]) => void;
  setQrNote: (note: string | null) => void;
}

export default function QrRouteResolver({
  setInventory,
  setIsOneInventory,
  setPlaceDetail,
  setPlacesForMap,
  setQrNote,
}: QrRouteResolverProps) {
  const { qrToken } = useParams<{ qrToken: string }>();
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      if (!qrToken) {
        setError("Neplatný odkaz QR.");
        setLoading(false);
        return;
      }

      let detail: QrAssignmentsDetail | null = null;
      let lastError: unknown = null;
      try {
        const { data } = await apollo.query<{ getQrAssignmentsDetail: QrAssignmentsDetail | null }>({
          query: GET_QR_ASSIGNMENTS_DETAIL,
          variables: { input: { qrId: qrToken } },
          fetchPolicy: "no-cache",
        });
        detail = data?.getQrAssignmentsDetail ?? null;
      } catch (e) {
        lastError = e;
      }

      if (cancelled) return;

      if (!detail?.place_id) {
        console.error("QR resolve error:", lastError);
        setError("Nepodařilo se načíst data z QR odkazu.");
        setLoading(false);
        return;
      }

      if (detail.event_id) {
        localStorage.setItem("eventId", detail.event_id);
      }

      const geo = geoFromQrDetail(detail);
      const placeName = detail.place_name || "Place";
      const minimalPlace: Place = {
        id: detail.place_id,
        name: placeName,
        geo_location: geo,
        loc_name: placeName,
      };
      setPlaceDetail(minimalPlace);
      setPlacesForMap([
        {
          id: detail.place_id,
          name: placeName,
          description: undefined,
          geo_location: geo,
        },
      ]);

      if (detail.inventory_id) {
        setQrNote(detail.note ?? null);
        setInventory([detail.inventory_id]);
        setIsOneInventory(true);
        navigate("/questions", { replace: true });
      } else {
        setInventory([]);
        setIsOneInventory(false);
        navigate(`/place/${detail.place_id}`, { replace: true });
      }
    };

    resolve().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [qrToken, apollo, navigate, setInventory, setIsOneInventory, setPlaceDetail, setPlacesForMap, setQrNote]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <AnimatedLogo loading="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Result
          status="error"
          title="QR odkaz nelze otevřít"
          subTitle={error}
          extra={
            <Button type="primary" onClick={() => navigate("/")}>
              Na hlavní stránku
            </Button>
          }
        />
      </div>
    );
  }

  return null;
}

